/* eslint-disable no-nested-ternary */
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import SimpleHeader from '@jobcase/react_pattern_library/components/SimpleHeader';
import SVGIcon from '@jobcase/react_pattern_library/components/SVGIcon';
import ArrowLeftRebrandRegularSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/ArrowLeftRebrandRegularSVG';
import Typography from '@jobcase/react_pattern_library/components/Typography/TypographyRebrand';
import LinkRebrand from '@jobcase/react_pattern_library/components/Link/LinkRebrand';
import TimesLightSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/TimesLightSVG';
import styles from '@jobcase/react_pattern_library/styles/rebrand';

const SimpleHeaderStyled = styled(SimpleHeader)`
  background-color: ${styles.color.jobcaseWhiteColor};
  border-bottom: 1px solid ${styles.color.dividerGrayColor};
  color: ${styles.color.jobcaseBlackColor};
  justify-content: space-between;
  left: 0;
  max-height: 57px;
  min-height: 57px;
  padding-left: ${styles.spacing.tiny}px;
  padding-right: ${styles.spacing.tiny}px;
  right: 0;
  top: 0;
  z-index: 21;

  ${({ isHeaderSticky }) => isHeaderSticky && css`
    position: fixed;
  `}

  ${({ filtersOverlay }) => filtersOverlay && css`
    background-color: ${styles.color.itemBgColor};
    border-bottom: 1px solid ${styles.color.dividerColor};
    max-height: 50px;
    min-height: 50px;

    @media (min-width: ${styles.breakpoint.md}) {
      border-bottom: none;
    }
  `}

  & > :first-child {
    width: fit-content;
  }

  @media (min-width: ${styles.breakpoint.md}) {
    max-height: 57px;
    min-height: 57px;
    position: relative;
    z-index: 1;

    &&.event-desktop-modal,
    &&.hub-desktop-modal,
    &&.job-desktop-modal,
    &&.permissions-desktop-modal {
      background-color: transparent;
      border-bottom: none;
      max-height: 64px;
      min-height: 64px;
      padding: 0 ${styles.spacing.medium}px;

      & > :first-child {
        display: none;
      }
    }

    &&.filters-desktop-modal {
      max-height: unset;
      min-height: unset;
      padding: ${styles.spacing.extraTiny * 5}px ${styles.spacing.extraTiny * 5}px ${styles.spacing.extraTiny * 3}px;
      position: sticky;
      top: 0;
    }

    &&.credit-card-desktop-modal {
      border-bottom: none;
      max-height: 64px;
      min-height: 64px;
    }
  }

  ${({ hasSecondaryText }) => (!hasSecondaryText && css`
    & > :last-child {
      height: 24px;
      width: 24px;
    }
  `)}

  ${({ filtersOverlay }) => filtersOverlay && css`
    background-color: ${styles.color.jobcaseWhiteColor};
    border-bottom: 1px solid ${styles.color.dividerGrayColor};
    max-height: 50px;
    min-height: 50px;

    @media (min-width: ${styles.breakpoint.md}) {
      border-bottom: none;
    }
  `}
`;

const BackLinkHeader = styled.div`
  cursor: pointer;
  display: flex;

  * {
    cursor: pointer;
    line-height: ${styles.font.lineHeight.body2};
  }
`;

const BackTypography = styled(Typography)`
  color: ${styles.color.jobcaseBlackColor};
  font-size: ${styles.font.fontSize.body};
  margin-left: ${styles.spacing.tiny}px;
`;

const SVGIconStyled = styled(SVGIcon)`
  cursor: pointer;
  height: 12px;
  width: 17px;

  path {
    fill: ${styles.color.textGrayColor};
  }

  @media (min-width: ${styles.breakpoint.md}) {
    &&.event-desktop-modal,
    &&.hub-desktop-modal,
    &&.job-desktop-modal,
    &&.permissions-desktop-modal {
      position: absolute;
      right: 36px;
      top: 36px;
    }

    &&.filters-desktop-modal {
      path {
        fill: ${styles.color.jobcaseBlackColor};
      }
    }

    &&.credit-card-desktop-modal {
      height: 24px;
      position: absolute;
      right: ${styles.spacing.small}px;
      top: ${styles.spacing.small}px;
      width: 24px;

      path {
        fill: ${styles.color.jobcaseBlackColor};
      }
    }
  }
`;

const HeaderText = styled(Typography)`
  color: ${styles.color.jobcaseBlackColor};
  font-family: ${styles.font.fontFamilyTitle};
  font-size: ${styles.font.fontSize.h3};
  font-weight: 500;
  line-height: ${styles.font.lineHeight.h3};
  margin: 0 ${styles.spacing.medium - 2}px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ filtersOverlay }) => filtersOverlay && css`
    color: ${styles.color.titleTextColor};
    font-weight: 500;
  `}

  @media(min-width: ${styles.breakpoint.md}) {
    font-size: 24px;
    font-weight: 400;
    line-height: ${styles.font.lineHeight.h2};
    margin: unset;
    width: unset !important;
  }
`;

const SecondaryTextWrapper = styled.div`
  cursor: pointer;

  ${({ filtersOverlay }) => filtersOverlay && css`
    color: ${styles.color.titleTextColor};
  `}
`;

const MobileModalHeaderRebrand = ({
  headerText,
  headerTextVariant,
  onClickBack,
  onClickClose,
  onClickSecondaryText,
  secondaryText,
  secondaryTextClass,
  closeFromLeft,
  className,
  filtersOverlay,
  isHeaderSticky,
  backTitle,
}) => (
  <SimpleHeaderStyled
    isHeaderSticky={isHeaderSticky}
    hasSecondaryText={secondaryText}
    className={[className, 'mobile-modal-header'].filter(val => val).join(' ')}
    filtersOverlay={filtersOverlay}
  >
    {onClickBack ? (
      <BackLinkHeader onClick={onClickBack}>
        <SVGIconStyled
          svgChild={(closeFromLeft && filtersOverlay) ? TimesLightSVG : filtersOverlay ? TimesLightSVG : ArrowLeftRebrandRegularSVG}
          size={SVGIcon.ICON_SIZE.LG}
          color={filtersOverlay ? styles.color.titleTextColor : styles.color.jobcaseWhiteColor}
          className="mobile-modal-header-back"
        />
        {backTitle && (
          <BackTypography variant={Typography.VARIANTS.BODY} className="back-title">{backTitle}</BackTypography>
        )}
      </BackLinkHeader>
    ) : !(className && filtersOverlay) ? <div /> : null}
    {headerText ? (
      <HeaderText className="mobile-modal-header-title" variant={headerTextVariant} filtersOverlay={filtersOverlay}>
        {headerText}
      </HeaderText>
    ) : <div />}
    {onClickClose ? (
      <SVGIconStyled
        svgChild={TimesLightSVG}
        size={SVGIcon.ICON_SIZE.LG}
        onClick={onClickClose}
        color={styles.color.bodyTextColor}
        className={[className, 'mobile-modal-header-close'].filter(val => val).join(' ')}
      />
    ) : secondaryText ? (
      <SecondaryTextWrapper
        onClick={onClickSecondaryText}
        className={[secondaryTextClass, 'mobile-modal-header-secondary-text'].filter(val => val).join(' ')}
        filtersOverlay={filtersOverlay}
      >
        {filtersOverlay ? <LinkRebrand className="mobile-modal-header-secondary-link">{secondaryText}</LinkRebrand> : secondaryText}
      </SecondaryTextWrapper>
    ) : (
      <div />
    )}
  </SimpleHeaderStyled>
);

MobileModalHeaderRebrand.propTypes = {
  headerText: PropTypes.string,
  secondaryText: PropTypes.string,
  secondaryTextClass: PropTypes.string,
  className: PropTypes.string,
  headerTextVariant: PropTypes.oneOf(Object.values(Typography.VARIANTS)),
  onClickBack: PropTypes.func,
  onClickClose: PropTypes.func,
  onClickSecondaryText: PropTypes.func,
  closeFromLeft: PropTypes.bool,
  filtersOverlay: PropTypes.bool,
  isHeaderSticky: PropTypes.bool,
  backTitle: PropTypes.string,
};

MobileModalHeaderRebrand.defaultProps = {
  headerText: null,
  headerTextVariant: Typography.VARIANTS.H3,
  onClickBack: null,
  onClickClose: null,
  onClickSecondaryText: null,
  secondaryText: null,
  secondaryTextClass: null,
  className: null,
  closeFromLeft: false,
  filtersOverlay: false,
  isHeaderSticky: false,
  backTitle: null,
};

export default MobileModalHeaderRebrand;
